import React, { useState, useEffect } from 'react';
import {
  Tag,
  TagVariant,
  Button,
  ButtonType,
  ButtonSize,
  IconName,
  DataTable,
  Pagination,
  Dropdown,
  DropdownSize,
  DropdownType,
} from '@hills-design-system/components';
import type { Column, DropdownOption } from '@hills-design-system/components';
import SectionTitle from '../../../components/SectionTitle/SectionTitle';
import { PetParentInfo, PetInfo, ValidationError } from '../../../types/petParent';
import SubmissionMethodModal from '../../../components/Modal/SubmissionMethodModal';
import SpreadsheetUploadModal, {
  ParsedUploadData,
} from '../../../components/Modal/SpreadsheetUploadModal';
import ManualEntryModal from '../../../components/Modal/ManualEntryModal';
import ReviewPetParentModal from '../../../components/Modal/ReviewPetParentModal';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';
import {
  usePetParentReportActions,
  usePetParentReportState,
} from '../../../hooks/usePetParentReports';
import { format, parseISO } from 'date-fns';
import {
  calculatePetParentReportingWindow,
  formatDueDateLabel,
  getDueDateTagVariant,
  getPetParentReportMondayDate,
} from '../../../utils/dateFormatHelpers';
import { useTranslation } from '../../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../../types/constants';
import { ReportStatus } from '../../../types/reports';
import Banner from '../../../components/Banner/Banner';
import { PaginatedPetParentReports, PetParentReport } from '../../../types/petParentReports';
import { formatTimeframeFromWeekOf } from '../../../utils/timeframeHelpers';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toZonedTime } from 'date-fns-tz';
import { Tooltip } from '../../../components/Tooltip';
import { useShelterState } from '../../../hooks/useShelter';
import { SuccessModal } from '../../../components/Modal';
import InitialReportingTable from './shared/InitialReportingTable';
import { analytics } from '../../../services/analytics';
import { BoltIcon } from '../../../components/icons';

type ModalType = 'manual' | 'review' | 'upload';

const PetParentReports: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isManualEntryModalOpen, setIsManualEntryModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<PetParentReport | null>(null);
  const [currentPetParentInfo, setCurrentPetParentInfo] = useState<PetParentInfo>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
  });
  const [currentPetInfos, setCurrentPetInfos] = useState<PetInfo[]>([]);
  const [parsedUploadData, setParsedUploadData] = useState<ParsedUploadData[] | undefined>(
    undefined
  );
  const [isDataFromSpreadsheet, setIsDataFromSpreadsheet] = useState(false);
  const [petIndexToEdit, setPetIndexToEdit] = useState<number | null>(null);
  const [isEditingSpreadsheetData, setIsEditingSpreadsheetData] = useState(false);
  const [lastEditedPetParentIndex, setLastEditedPetParentIndex] = useState<number | null>(null);
  const [petParentToDelete, setPetParentToDelete] = useState<number | null>(null);
  const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] = useState(false);
  const [modalToClose, setModalToClose] = useState<ModalType | null>(null);

  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isFromHomePage, setIsFromHomePage] = useState(false);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  const itemsPerPage = 10;

  const petParentReports: PaginatedPetParentReports | null = usePetParentReportState();
  const {
    getPetParentReports,
    submitPetParentReport,
    uploadPetParentReportSpreadsheet,
    downloadTemplate,
    downloadOptinGuide,
  } = usePetParentReportActions();
  const loading = petParentReports === null;

  const { t } = useTranslation();
  const { shelter } = useShelterState();
  const navigate = useNavigate();

  useEffect(() => {
    // set page title
    document.title = t(TranslationKeyEnum.PetParentReportsSectionTitle);
  }, [t]);

  useEffect(() => {
    getPetParentReports(selectedFilter, currentPage, itemsPerPage);
  }, [getPetParentReports, selectedFilter, currentPage]);

  const filterOptions: DropdownOption[] = [
    { id: 'all', label: t(TranslationKeyEnum.AllReports) },
    { id: 'submitted', label: t(TranslationKeyEnum.Submitted) },
    { id: 'missed', label: t(TranslationKeyEnum.Missed) },
    { id: 'late', label: t(TranslationKeyEnum.Late) },
  ];

  const handleFilterChange = (selectedId: string) => {
    setSelectedFilter(selectedId);
    setCurrentPage(1);
  };

  const missedReportsAll: PetParentReport[] = petParentReports
    ? petParentReports.data.filter(
      (report) => report.status === 'missed' && report.canSubmit === true
    )
    : [];

  // Identify a due-soon report (<= 3 days to deadline) for yellow warning banner
  const dueSoonReport: PetParentReport | undefined = petParentReports
    ? petParentReports.data.find((report) => report.status === 'due' && report.isWarning === true)
    : undefined;

  const openModal = (report: PetParentReport) => {
    analytics.ctaClick('start', 'pet parent', report.timeframe);
    setSelectedReport(report);
    setIsModalOpen(true);
  };

  // Handle auto-opening modal when coming from home page
  useEffect(() => {
    const reportId = searchParams.get('reportId');
    if (reportId && petParentReports?.data && !isModalOpen) {
      const targetReport = petParentReports.data.find((report) => report.id === reportId);
      if (targetReport) {
        setIsFromHomePage(true);
        openModal(targetReport);
        // Remove the reportId parameter from URL after opening modal
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('reportId');
        setSearchParams(newSearchParams);
      }
    }
  }, [petParentReports?.data, searchParams, setSearchParams, isModalOpen]);

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedReport(null);
    navigateToHomePage();
  };

  const navigateToHomePage = () => {
    // navigate to home page if modal closed and user is coming from the home page
    if (isFromHomePage) {
      setIsFromHomePage(false);
      navigate(`/`);
    }
  };

  const openUploadModal = () => {
    analytics.ctaClick('excel bulk', 'pet parent', selectedReport?.timeframe ?? '');
    setIsModalOpen(false);
    setIsUploadModalOpen(true);
  };

  const openManualEntryModal = () => {
    analytics.ctaClick('manual input', 'pet parent', selectedReport?.timeframe ?? '');
    setValidationErrors([]);
    setIsModalOpen(false);
    setPetIndexToEdit(null);
    setIsManualEntryModalOpen(true);
  };

  const handleCloseModal = (modalType: ModalType) => {
    if (
      (modalType === 'manual' && !isManualEntryModalOpen) ||
      (modalType === 'review' && !isReviewModalOpen) ||
      (modalType === 'upload' && !isUploadModalOpen)
    ) {
      return;
    }

    let hasUnsavedData = false;

    if (modalType === 'manual') {
      // No unsaved data for manual entry modal
    } else if (modalType === 'review') {
      hasUnsavedData = !!parsedUploadData || currentPetInfos.length > 0;
    } else if (modalType === 'upload') {
      if (isUploading || isProcessing) {
        return;
      }
      hasUnsavedData = uploadProgress > 0;
    }

    if (hasUnsavedData) {
      setModalToClose(modalType);
      setIsCloseConfirmationOpen(true);
    } else {
      setModalToClose(modalType);
      confirmCloseModal(modalType);
    }

    setIsSubmitting(false);
  };

  const confirmCloseModal = (modalType: ModalType) => {
    if (modalType === 'manual') {
      setIsManualEntryModalOpen(false);
      setPetIndexToEdit(null);
      setIsEditingSpreadsheetData(false);
    } else if (modalType === 'review') {
      setIsReviewModalOpen(false);
      setLastEditedPetParentIndex(null);
      setParsedUploadData(undefined);
      setIsDataFromSpreadsheet(false);
      setCurrentPetParentInfo({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip: '',
      });
      setCurrentPetInfos([]);
    } else if (modalType === 'upload') {
      setIsUploadModalOpen(false);
      setUploadProgress(0);
      setIsProcessing(false);
      setIsUploading(false);
    }

    setIsCloseConfirmationOpen(false);
    setModalToClose(null);
  };

  const closeManualEntryModal = () => {
    setIsManualEntryModalOpen(false);
    setPetIndexToEdit(null);
    setIsEditingSpreadsheetData(false);
  };

  const handleFileUpload = () => {
    openUploadModal();
  };

  const handleManualEntry = () => {
    openManualEntryModal();
  };

  const handleReportZero = async () => {
    if (!selectedReport) {
      closeModal();
      return;
    }

    try {
      const result = await submitPetParentReport(selectedReport.timeframe_start, []);

      if (result.success) {
        setIsSubmitSuccess(true);
        getPetParentReports(selectedFilter, currentPage, itemsPerPage);
      }
    } catch (error) {
      console.error('Error submitting zero report:', error);
    }

    closeModal();
  };

  const handleContinueUpload = async (
    file: File | null,
    onProgress?: (progress: number) => void
  ) => {
    if (file && selectedReport) {
      try {
        setUploadProgress(0);
        setIsProcessing(false);
        setIsUploading(true);
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => {
            const newProgress = Math.min(prev + 10, 90);
            onProgress?.(newProgress);
            return newProgress;
          });
        }, 200);

        const result = await uploadPetParentReportSpreadsheet(selectedReport.timeframe_start, file);
        clearInterval(progressInterval);
        setUploadProgress(100);
        onProgress?.(100);
        setIsProcessing(true);

        if (result.success) {
          if (result.data && result.data.length > 0) {
            setParsedUploadData(result.data);
            setIsDataFromSpreadsheet(true); // Mark as coming from spreadsheet
            setIsProcessing(false);
            setIsUploading(false);
            setIsUploadModalOpen(false);
            setIsReviewModalOpen(true);
          } else {
            setIsProcessing(false);
            setIsUploading(false);
            setIsUploadModalOpen(false);
          }
          getPetParentReports(selectedFilter, currentPage, itemsPerPage);
        } else {
          setIsProcessing(false);
          setIsUploading(false);
          setUploadProgress(0);

          let errorMessage = 'Upload failed';
          if (result.error) {
            if (typeof result.error === 'string') {
              errorMessage = result.error;
            } else if (result.error instanceof Error) {
              errorMessage = result.error.message;
            } else if (
              typeof result.error === 'object' &&
              result.error !== null &&
              'message' in result.error
            ) {
              errorMessage = result.error.message as string;
            }
          }
          // Track spreadsheet upload error
          analytics.customError(
            'pet_parent',
            `Spreadsheet upload failed: ${errorMessage}`,
            selectedReport?.timeframe ?? ''
          );
          throw new Error(errorMessage);
        }
      } catch (error) {
        console.error('Upload error:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown upload error';
        // Track spreadsheet upload error
        analytics.customError(
          'pet_parent',
          `Spreadsheet upload error: ${errorMessage}`,
          selectedReport?.timeframe ?? ''
        );
        setUploadProgress(0);
        setIsProcessing(false);
        setIsUploading(false);
        throw error;
      }
    }
  };

  const handleEditPetParent = (petParentIndex: number) => {
    setPetIndexToEdit(petParentIndex);
    setIsEditingSpreadsheetData(!!parsedUploadData);
    setLastEditedPetParentIndex(null);
    setIsReviewModalOpen(false);
    setIsManualEntryModalOpen(true);
  };

  const handleDeletePetParent = (petParentIndex: number) => {
    setPetParentToDelete(petParentIndex);
    setIsDeleteConfirmationOpen(true);
  };

  const confirmDeletePetParent = () => {
    analytics.ctaClick('delete record', 'pet parent', selectedReport?.timeframe ?? '');
    let allRecordsDeleted = false;

    if (petParentToDelete !== null) {
      if (parsedUploadData) {
        const updatedParsedData = parsedUploadData.filter(
          (_, index) => index !== petParentToDelete
        );
        setParsedUploadData(updatedParsedData.length > 0 ? updatedParsedData : undefined);
        if (updatedParsedData.length === 0) {
          setIsDataFromSpreadsheet(false);
          allRecordsDeleted = true;
        }
      } else {
        // For manual entry, we only have one record, so deleting it means all are deleted
        setCurrentPetParentInfo({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          state: '',
          zip: '',
        });
        setCurrentPetInfos([]);
        allRecordsDeleted = true;
      }
    }

    setPetParentToDelete(null);
    setIsDeleteConfirmationOpen(false);

    // If all records have been deleted, close review modal and return to source modal
    if (allRecordsDeleted) {
      setIsReviewModalOpen(false);
      // Return to the appropriate modal based on the source
      if (isDataFromSpreadsheet) {
        setIsUploadModalOpen(true);
      } else {
        setIsManualEntryModalOpen(true);
      }
    }
  };

  const handleSubmitReport = async () => {
    if (!selectedReport) {
      setIsReviewModalOpen(false);
      setIsSubmitting(false);
      return;
    }
    try {
      const dataToSubmit =
        parsedUploadData ??
        (currentPetInfos.length > 0
          ? [
            {
              petParentInfo: currentPetParentInfo,
              petInfos: currentPetInfos,
              validationErrors: [],
            },
          ]
          : []);
      setIsSubmitting(true);
      const result = await submitPetParentReport(selectedReport.timeframe_start, dataToSubmit);

      if (result.success) {
        analytics.ctaClick('submit', 'pet parent', selectedReport.timeframe);
        setIsSubmitSuccess(true);
        getPetParentReports(selectedFilter, currentPage, itemsPerPage);
        setParsedUploadData(undefined);
        setIsDataFromSpreadsheet(false);
        setCurrentPetParentInfo({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          state: '',
          zip: '',
        });
        setCurrentPetInfos([]);
      } else {
        // Track submission failure
        const submissionType = isDataFromSpreadsheet ? 'bulk spreadsheet' : 'manual entry';
        analytics.customError(
          'pet_parent',
          `Submission failed (${submissionType}): ${result.error ?? 'Unknown error'}`,
          selectedReport.timeframe
        );
      }
    } catch (error) {
      // Track submission exception
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const submissionType = isDataFromSpreadsheet ? 'bulk spreadsheet' : 'manual entry';
      analytics.customError(
        'pet_parent',
        `Submission exception (${submissionType}): ${errorMessage}`,
        selectedReport.timeframe
      );
    } finally {
      setIsSubmitting(false);
    }
    setIsReviewModalOpen(false);
  };

  const handleAddMorePetParents = () => {
    setPetIndexToEdit(null);
    setIsReviewModalOpen(false);
    setIsManualEntryModalOpen(true);
  };

  const handleCloseManualEntryToReview = () => {
    setIsManualEntryModalOpen(false);
    setIsReviewModalOpen(true);
  };

  const handleManualEntrySubmit = (petParentInfo: PetParentInfo, petInfos: PetInfo[]) => {
    setValidationErrors([]);

    if (petIndexToEdit !== null) {
      if (isEditingSpreadsheetData && parsedUploadData) {
        const updatedParsedData = [...parsedUploadData];
        updatedParsedData[petIndexToEdit] = {
          petParentInfo,
          petInfos,
          validationErrors: [],
        };
        setParsedUploadData(updatedParsedData);
      } else {
        setCurrentPetParentInfo(petParentInfo);
        setCurrentPetInfos(petInfos);
      }
      setLastEditedPetParentIndex(petIndexToEdit);
      setPetIndexToEdit(null);
      setIsEditingSpreadsheetData(false);
    } else if (parsedUploadData) {
      // Check if we already have multiple pet parents (parsedUploadData exists)
      // Add new pet parent to existing parsedUploadData
      const newEntry: ParsedUploadData = {
        petParentInfo,
        petInfos,
        validationErrors: [],
      };
      setParsedUploadData([...parsedUploadData, newEntry]);
      // Keep the existing spreadsheet flag state when adding to existing data
    } else {
      // Handle single pet parent scenario
      const isFirstEntry = currentPetInfos.length === 0;
      const isDifferentPetParent =
        petParentInfo.email.toLowerCase() !== currentPetParentInfo.email.toLowerCase();

      if (isFirstEntry) {
        // First entry - just set the current pet parent and pets
        setCurrentPetParentInfo(petParentInfo);
        setCurrentPetInfos(petInfos);
      } else if (isDifferentPetParent) {
        // Different pet parent - convert current data to parsedUploadData format and add new entry
        const currentEntry: ParsedUploadData = {
          petParentInfo: currentPetParentInfo,
          petInfos: currentPetInfos,
          validationErrors: [],
        };

        const newEntry: ParsedUploadData = {
          petParentInfo,
          petInfos,
          validationErrors: [],
        };

        // Convert to parsedUploadData format with multiple pet parents
        setParsedUploadData([currentEntry, newEntry]);
        setIsDataFromSpreadsheet(false); // Mark as manual entry, not from spreadsheet

        // Clear single pet parent state since we're now using parsedUploadData
        setCurrentPetParentInfo({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          state: '',
          zip: '',
        });
        setCurrentPetInfos([]);
      } else {
        // Same pet parent - add pets to existing list (avoiding duplicates)
        const isPetDuplicate = (newPet: PetInfo, existingPet: PetInfo) => {
          return (
            newPet.name.toLowerCase() === existingPet.name.toLowerCase() &&
            newPet.species === existingPet.species &&
            String(newPet.adoptionDate) === String(existingPet.adoptionDate)
          );
        };

        const uniqueNewPets = petInfos.filter(
          (newPet) => !currentPetInfos.some((existingPet) => isPetDuplicate(newPet, existingPet))
        );

        setCurrentPetInfos([...currentPetInfos, ...uniqueNewPets]);
      }
    }

    closeManualEntryModal();
    setIsReviewModalOpen(true);
  };

  const handleDownloadTemplate = async (format: 'xlsx' | 'csv' = 'xlsx') => {
    analytics.ctaClick(
      format === 'xlsx' ? 'xlsx' : 'csv',
      'pet parent',
      selectedReport?.timeframe ?? ''
    );
    await downloadTemplate(format);
  };

  const submissionOptions = [
    {
      icon: IconName.Upload,
      label: t(TranslationKeyEnum.UploadSpreadsheet),
      onClick: handleFileUpload,
      isPrimary: true,
    },
    {
      icon: IconName.Edit,
      label: t(TranslationKeyEnum.ManualEntry),
      onClick: handleManualEntry,
    },
  ];

  const isPetszelEnabled = shelter?.is_petszel_enabled === true;
  const petszelFirstUsedAt = shelter?.petszel_first_used_at;

  // Determine if shelter is Canadian for form display
  const isCanadian = shelter?.country === 'CA' || false;

  // Helper function to check if a report falls within the petszel-managed window
  const isPetszelManagedReport = (timeframeStart: string): boolean => {
    if (!petszelFirstUsedAt) return false;
    const firstUsedDate = new Date(petszelFirstUsedAt);
    const reportDate = new Date(timeframeStart);

    // Set both dates to midnight to compare only the date portion
    firstUsedDate.setHours(0, 0, 0, 0);
    reportDate.setHours(0, 0, 0, 0);

    // Calculate the end of the report's week (Monday + 6 days = Sunday)
    const weekEndDate = new Date(reportDate);
    weekEndDate.setDate(weekEndDate.getDate() + 6);

    // Check if petszel_first_used_at falls within the report's week OR if the week starts after it
    return (firstUsedDate >= reportDate && firstUsedDate <= weekEndDate) || reportDate > firstUsedDate;
  };

  const reportColumns: Column[] = [
    {
      header: t(TranslationKeyEnum.ReportTimeframe),
      accessor: 'timeframe',
      renderCell: (value: string) => {
        return formatTimeframeFromWeekOf(value);
      },
    },
    {
      header: t(TranslationKeyEnum.Status),
      accessor: 'status',
      renderCell: (value: string, row: PetParentReport) => {
        // Check if this report is in the petszel-managed window
        if (shelter?.is_petszel_enabled && isPetszelManagedReport(row.timeframe_start)) {
          return <Tag label={t(TranslationKeyEnum.AutomatedSync)} variant={TagVariant.Green} />;
        }

        // Show existing statuses for months before petszel_first_used_at
        if (value === 'due') {
          const dueDateLabel = formatDueDateLabel(row.dueDate, t);
          const tagVariant = getDueDateTagVariant(value, row.isWarning);
          return <Tag label={dueDateLabel} variant={tagVariant} />;
        } else if (value === 'submitted') {
          return <Tag label={t(TranslationKeyEnum.Submitted)} variant={TagVariant.Green} />;
        } else if (value === 'missed') {
          return <Tag label={t(TranslationKeyEnum.Missed)} variant={TagVariant.Red} />;
        } else if (value === 'late') {
          // Late submissions displayed in green per spec
          return <Tag label={t(TranslationKeyEnum.LateSubmission)} variant={TagVariant.Green} />;
        }
        return value;
      },
    },
    {
      header: t(TranslationKeyEnum.UniquePetParentOptIns),
      accessor: 'optIns',
      renderCell: (value: string | undefined, row: PetParentReport) => {
        if (value === undefined) {
          if (shelter?.is_petszel_enabled && isPetszelManagedReport(row.timeframe_start)) {
            return t(TranslationKeyEnum.TotalOptInsPlural, 0);
          }
          return '-';
        }

        const count = parseInt(value, 10);
        if (isNaN(count)) {
          return value;
        } else if (count === 1) {
          return t(TranslationKeyEnum.TotalOptInsSingular, count);
        } else {
          return t(TranslationKeyEnum.TotalOptInsPlural, count);
        }
      },
    },
    {
      header: t(TranslationKeyEnum.TotalOptIns),
      accessor: 'totalOptIns',
      renderCell: (value: string | undefined, row: PetParentReport) => {
        if (value === undefined) {
          if (shelter?.is_petszel_enabled && isPetszelManagedReport(row.timeframe_start)) {
            return t(TranslationKeyEnum.TotalPetsPlural, 0);
          }
          return '-';
        }

        const count = parseInt(value, 10);
        if (isNaN(count)) {
          return value;
        } else if (count === 1) {
          return t(TranslationKeyEnum.TotalPetsSingular, count);
        } else {
          return t(TranslationKeyEnum.TotalPetsPlural, count);
        }
      },
    },
    {
      header: t(TranslationKeyEnum.LastModified),
      accessor: 'lastModified',
      renderCell: (value: string, row: PetParentReport) => {
        if (row.optIns === undefined) {
          return '-';
        }

        if (!value) return value;
        try {
          const dateObj = parseISO(value);
          return format(dateObj, 'MMM. d, yyyy');
        } catch {
          return value;
        }
      },
    },
    ...(isPetszelEnabled
      ? []
      : [
        {
          header: (
            <span className="sr-only">{t(TranslationKeyEnum.Actions)}</span>
          ) as unknown as string,
          accessor: 'actions',
          align: 'right' as const,
          renderCell: (_value: unknown, row: PetParentReport) => {
            // Hide button for missed reports that cannot be submitted (past late deadline)
            if (row.status === ReportStatus.Missed && row.canSubmit !== true) {
              return (
                <Tooltip
                  align="left"
                  content={
                    <div className="w-[360px]  p-4">
                      <div className="text-sm font-extralight text-white leading-[1.375]">
                        {t(TranslationKeyEnum.PetParentReportsSubmitDeadlineTooltip)}
                      </div>
                    </div>
                  }
                />
              );
            }

            // Hide button for submitted reports that cannot add more
            if (row.status === ReportStatus.Submitted && !row.canAddMore) {
              return null;
            }

            // Show button ONLY for due, submitted (with canAddMore), and actionable missed reports
            // Do NOT show for 'late' (a report has already been submitted late)
            if (
              row.status !== ReportStatus.Due &&
              row.status !== ReportStatus.Submitted &&
              !(row.status === ReportStatus.Missed && row.canSubmit === true)
            ) {
              return null;
            }

            const isAddMore = row.status === ReportStatus.Submitted && row.canAddMore === true;
            const buttonLabel = isAddMore
              ? t(TranslationKeyEnum.AddMore)
              : t(TranslationKeyEnum.Start);
            const buttonType = isAddMore ? ButtonType.Secondary : ButtonType.Primary;

            return (
              <Button
                label={buttonLabel}
                type={buttonType}
                size={ButtonSize.Small}
                onClick={() => openModal(row)}
                className="!w-32"
              />
            );
          },
        },
      ]),
  ];

  const totalPages = petParentReports ? petParentReports.totalPages : 0;
  const paginatedData = petParentReports ? petParentReports.data : [];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="w-full bg-white rounded-2xl border border-black/20 p-12">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <SectionTitle title={t(TranslationKeyEnum.PetParentReportsSectionTitle)} subtitle="" />
          <Tooltip
            content={
              <div className="w-[360px]  p-4">
                {
                  // manual shelter message
                  shelter && !shelter.is_petszel_enabled && (
                    <div className="space-y-3">
                      <div className="text-sm font-extralight text-white leading-[1.375]">
                        {t(TranslationKeyEnum.PetParentReportsSubmitTooltipTitle)}
                      </div>
                      <div className="text-sm font-extralight text-white leading-[1.375]">
                        {t(TranslationKeyEnum.PetParentReportsSubmitTooltipDescription)}
                      </div>
                      <hr className="opacity-20" />
                      <div className="text-sm font-bold text-white leading-[1.375]">
                        {t(TranslationKeyEnum.PetParentReportsTooltipTitle)}
                      </div>
                      <div className="text-sm font-extralight text-white leading-[1.375]">
                        {t(TranslationKeyEnum.PetParentReportsTooltipDescription1)}{' '}
                        <button
                          className="underline bg-transparent p-0 m-0"
                          onClick={() => {
                            analytics.ctaClick(
                              'opt-in guide',
                              'pet parent',
                              t(TranslationKeyEnum.HillsOptInGuide) ?? ''
                            );
                            downloadOptinGuide();
                          }}
                        >
                          {t(TranslationKeyEnum.HillsOptInGuide)}
                        </button>{' '}
                        {t(TranslationKeyEnum.PetParentReportsTooltipDescription2)}
                      </div>
                    </div>
                  )
                }
                {
                  // petszel shelter message
                  shelter && shelter.is_petszel_enabled && (
                    <>
                      <div className="flex items-start gap-2 mb-3">
                        <BoltIcon width={10} height={15} fill="white" className="mt-0.5 flex-shrink-0" />
                        <div className="text-sm font-normal text-white leading-[1.375]">
                          {t(TranslationKeyEnum.PetszelPetParentReportTooltipTitle)}
                        </div>
                      </div>
                      <div className="text-sm font-normal text-white leading-[1.375] ml-4">
                        {t(TranslationKeyEnum.PetszelReportsTooltipDescription).split('Automation Guide')[0]}
                        <button
                          className="underline bg-transparent p-0 m-0"
                          onClick={() => {
                            analytics.ctaClick(
                              'opt-in guide',
                              'pet parent',
                              t(TranslationKeyEnum.HillsOptInGuide) ?? ''
                            );
                            downloadOptinGuide();
                          }}
                        >
                          {t(TranslationKeyEnum.HillsOptInGuide)}
                        </button>
                      </div>
                    </>
                  )
                }

              </div>
            }
          />
        </div>

        {
          !isPetszelEnabled && <div style={{ position: 'relative', width: '320px' }}>
            <Dropdown
              ariaLabel={t(TranslationKeyEnum.FilterReportsByStatusAriaLabel)}
              placeholder={t(TranslationKeyEnum.AllReports)}
              options={filterOptions}
              selectedId={selectedFilter}
              onChange={handleFilterChange}
              type={DropdownType.Secondary}
              size={DropdownSize.Medium}
              prefixIcon={IconName.Tune}
            />
          </div>
        }

      </div>

      {!loading && !isPetszelEnabled && missedReportsAll.length > 0 && (
        <div className="mb-6">
          {<Banner
            variant="danger"
            title={
              missedReportsAll.length === 1
                ? t(TranslationKeyEnum.MissedReportSingular)
                : t(TranslationKeyEnum.MissedReportPlural, missedReportsAll.length)
            }
            subtitle={t(TranslationKeyEnum.SubmitNowToStayInGoodStanding)}
            className="mb-6"
          /> }
        </div>
      )}
      {!loading && !isPetszelEnabled && dueSoonReport && (
        <div className="mb-6">
          <Banner
            variant="warning"
            title={t(TranslationKeyEnum.AddNewPetParentsForTimeframe, dueSoonReport.timeframe, dueSoonReport.dueDate
              ? format(toZonedTime(dueSoonReport.dueDate, 'UTC'), 'EEEE, MMM. d')
              : '')}
            className="mb-6"
          />
        </div>
      )}

      {(() => {
        if (loading) {
          return (
            <div className="flex justify-center items-center h-40">
              {t(TranslationKeyEnum.LoadingPetParentReports)}
            </div>
          );
        }
        if (paginatedData.length > 0) {
          return (
            <DataTable className="!overflow-visible" columns={reportColumns} data={paginatedData} />
          );
        }
        // Only show the "First Report Due" message when no filter is active
        // When a filter is active with no results, show an empty table instead
        if (selectedFilter === 'all') {
          return (
            <InitialReportingTable
              header={t(
                TranslationKeyEnum.NoPetParentReportsHeader,
                calculatePetParentReportingWindow(shelter?.join_program_date),
                getPetParentReportMondayDate(shelter?.join_program_date)
              )}
              description={t(TranslationKeyEnum.NoPetParentReportsDescription)}
            />
          );
        }
        // Show empty table when filter is active but no results
        return <DataTable className="!overflow-visible" columns={reportColumns} data={[]} />;
      })()}

      <div className="flex justify-center mt-12">
        {paginatedData.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <SubmissionMethodModal
        isOpen={isModalOpen}
        onClose={closeModal}
        options={submissionOptions}
        reportZeroText={t(TranslationKeyEnum.ReportZeroPetParents)}
        onReportZero={handleReportZero}
        title={t(TranslationKeyEnum.SelectSubmissionMethod)}
        eyebrow={t(TranslationKeyEnum.PetParentReport)}
        secondaryEyebrow={selectedReport ? t(TranslationKeyEnum.WeekOfTimeframe, selectedReport.timeframe) : ''}
        reportStatus={selectedReport?.status}
        isCanadian={isCanadian}
      />

      <SpreadsheetUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => handleCloseModal('upload')}
        onContinue={handleContinueUpload}
        onDownloadTemplate={handleDownloadTemplate}
        title={t(TranslationKeyEnum.UploadSpreadsheet)}
        eyebrow={t(TranslationKeyEnum.PetParentReport)}
        secondaryEyebrow={selectedReport ? t(TranslationKeyEnum.WeekOfTimeframe, selectedReport.timeframe) : undefined}
        reportTimeframe={selectedReport?.timeframe}
        uploadProgress={uploadProgress}
        isProcessing={isProcessing}
      />

      <ManualEntryModal
        isOpen={isManualEntryModalOpen}
        onClose={() => handleCloseModal('manual')}
        onSubmit={handleManualEntrySubmit}
        title={
          petIndexToEdit !== null
            ? t(TranslationKeyEnum.EditPetParent)
            : t(TranslationKeyEnum.AddNewPetParent)
        }
        eyebrow={t(TranslationKeyEnum.PetParentReport)}
        secondaryEyebrow={selectedReport ? t(TranslationKeyEnum.WeekOfTimeframe, selectedReport.timeframe) : ''}
        reportTimeframe={selectedReport?.timeframe}
        isEditing={petIndexToEdit !== null}
        initialPetParentInfo={
          petIndexToEdit !== null
            ? isEditingSpreadsheetData && parsedUploadData?.[petIndexToEdit]
              ? parsedUploadData[petIndexToEdit].petParentInfo
              : currentPetParentInfo
            : undefined
        }
        initialPetInfos={
          petIndexToEdit !== null
            ? isEditingSpreadsheetData && parsedUploadData?.[petIndexToEdit]
              ? parsedUploadData[petIndexToEdit].petInfos
              : currentPetInfos
            : undefined
        }
        validationErrors={validationErrors}
        isFromReviewModal={parsedUploadData !== undefined || currentPetInfos.length > 0}
        onCloseToReview={handleCloseManualEntryToReview}
        onDelete={handleDeletePetParent}
        parsedUploadData={parsedUploadData}
        editIndex={petIndexToEdit}
        isCanadian={isCanadian}
      />

      <ReviewPetParentModal
        isOpen={isReviewModalOpen}
        onClose={() => handleCloseModal('review')}
        reportTimeframe={selectedReport ? formatTimeframeFromWeekOf(selectedReport.timeframe) : ''}
        parsedData={
          parsedUploadData ??
          (currentPetInfos.length > 0
            ? [
              {
                petParentInfo: currentPetParentInfo,
                petInfos: currentPetInfos,
                validationErrors: [],
              },
            ]
            : [])
        }
        onSubmit={handleSubmitReport}
        onEdit={handleEditPetParent}
        onDelete={handleDeletePetParent}
        onAddMore={handleAddMorePetParents}
        isFromSpreadsheet={isDataFromSpreadsheet}
        scrollToPetParentIndex={lastEditedPetParentIndex}
        title={t(TranslationKeyEnum.ReviewPetParentInformation)}
        eyebrow={t(TranslationKeyEnum.PetParentReport)}
        secondaryEyebrow={selectedReport ? t(TranslationKeyEnum.WeekOfTimeframe, selectedReport.timeframe) : ''}
        isSubmitting={isSubmitting}
        isCanadian={isCanadian}
      />

      <ConfirmationModal
        isOpen={isDeleteConfirmationOpen}
        onClose={() => {
          setIsDeleteConfirmationOpen(false);
          setPetParentToDelete(null);
        }}
        title={t(TranslationKeyEnum.DeleteThisEntry)}
        message={t(TranslationKeyEnum.DeleteEntryMessage)}
        confirmLabel={t(TranslationKeyEnum.YesDelete)}
        cancelLabel={t(TranslationKeyEnum.GoBack)}
        onConfirm={confirmDeletePetParent}
        variant="default"
      />

      <ConfirmationModal
        isOpen={isCloseConfirmationOpen}
        onClose={() => {
          setIsCloseConfirmationOpen(false);
          setModalToClose(null);
        }}
        title={t(TranslationKeyEnum.DiscardReport)}
        message={t(TranslationKeyEnum.DiscardPetParentReportMessage)}
        confirmLabel={t(TranslationKeyEnum.YesDiscard)}
        cancelLabel={t(TranslationKeyEnum.GoBack)}
        onConfirm={() => confirmCloseModal(modalToClose as ModalType)}
        variant="default"
      />

      <SuccessModal
        message={t(TranslationKeyEnum.PetParentReportSuccessHeader)}
        backButtonLabel={t(TranslationKeyEnum.BackToAllReports)}
        isOpen={isSubmitSuccess}
        onClose={() => {
          setIsSubmitSuccess(false);
        }}
        onBackClick={() => {
          setIsSubmitSuccess(false);
        }}
      />
    </div>
  );
};

export default PetParentReports;
